'use client'

import * as React from 'react'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@ui/web/components'
import { StickyBanner as StickyBannerComponent, typographyVariants } from '@ui/web/lib'
import { useLocalStorage } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../context'

interface StickyBannerProps {
  className?: string
  type: 'marketing' | 'school' | 'web'
}

export function StickyBanner({ className, type }: StickyBannerProps) {
  const trpc = useSharedTRPC()

  const { data } = useQuery({
    ...trpc.marketing.getLabel.queryOptions(type),
    staleTime: 1000 * 60 * 60 * 2,
  })

  const [membershipClosed, setMembershipClosed] = useLocalStorage(
    `pibo-sticky-banner-${type}-membership-is-closed`,
    false,
  )
  const [marketingClosed, setMarketingClosed] = useLocalStorage(`pibo-sticky-banner-${type}-marketing-is-closed`, false)

  const visibleBanners = useMemo(() => {
    const [membershipBanner, marketingBanner] = data ?? [null, null]
    return [
      // DISABLED: the premium unlock for teachers is not shipping, so the membership banner no
      // longer reports x/N progress toward its threshold. Commented out, not deleted, so it can
      // come back with the feature; `marketing.getLabel` still returns the field.
      // Only the membership banner carries `progress`; the marketing one is pure CMS content.
      // A zero target is dropped here rather than in the bar, so the division below cannot see it.
      ...(membershipBanner && !membershipClosed
        ? [
            {
              banner: membershipBanner,
              // progress: membershipBanner.progress?.target ? membershipBanner.progress : null,
              onClose: setMembershipClosed,
              key: 'membership' as const,
            },
          ]
        : []),
      ...(marketingBanner && !marketingClosed
        ? [{ banner: marketingBanner, /* progress: null, */ onClose: setMarketingClosed, key: 'marketing' as const }]
        : []),
    ]
  }, [data, membershipClosed, marketingClosed, setMembershipClosed, setMarketingClosed])

  if (visibleBanners.length === 0) return null

  return (
    <div className='flex flex-col'>
      {visibleBanners.map(({ banner, onClose, key }) => (
        <StickyBannerComponent
          key={key}
          onClose={() => onClose(true)}
          className={cn(
            key === 'marketing' ? 'srd:bg-purple-100 srd:text-neutral-white' : 'srd:bg-blue-30 srd:text-navy-80',
            className,
          )}
        >
          <div className='srd:flex srd:max-w-[90%] srd:flex-wrap srd:items-center srd:justify-center srd:gap-x-3 srd:gap-y-1'>
            <p
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:mx-0 srd:overflow-x-auto srd:drop-shadow-md',
              )}
            >
              {banner.text}
            </p>

            {/* DISABLED: the premium unlock for teachers is not shipping, so the x/N bar toward its
                threshold never renders. Commented out, not deleted, so it can come back. */}
            {/*
            {progress && (
              <span className='srd:flex srd:items-center srd:gap-2'>
                <span
                  role='progressbar'
                  aria-valuenow={progress.current}
                  aria-valuemin={0}
                  aria-valuemax={progress.target}
                  aria-label={`${progress.current} dari ${progress.target} murid Premium`}
                  className='srd:bg-neutral-white/50 srd:h-2 srd:w-24 srd:overflow-hidden srd:rounded-full'
                >
                  <span
                    className='srd:bg-blue-100 srd:block srd:h-full srd:rounded-full'
                    style={{
                      width: `${Math.min(100, Math.max(0, (progress.current / progress.target) * 100))}%`,
                    }}
                  />
                </span>

                <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:shrink-0 srd:text-current')}>
                  {progress.current}/{progress.target}
                </span>
              </span>
            )}
            */}

            <Button baseButton size='xs' variant='outline' className='srd:text-current' asChild>
              <a href={banner.cta.link} target='_blank'>
                {banner.cta.text}
              </a>
            </Button>
          </div>
        </StickyBannerComponent>
      ))}
    </div>
  )
}
